import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { WorkflowData, WorkflowFlow, WorkflowContextType, WorkflowPhase, WorkflowStep, WorkflowHistoryEntry, BlockToggleResult } from '@/types/workflow';
import AlertDialog from '@/components/ui/AlertDialog';

interface WorkflowProviderProps {
  children: ReactNode;
  onSave?: (workflows: WorkflowData) => void | Promise<void>;
}

type ConfirmationDialogState = {
  isOpen: boolean;
  type: 'save' | null;
  onConfirm: () => void;
  onCancel: () => void;
};

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined);

export function WorkflowProvider({ children, onSave }: WorkflowProviderProps) {
  const [workflows, setWorkflows] = useState<WorkflowData>([]);
  const [currentFlow, setCurrentFlow] = useState<WorkflowFlow | null>(null);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState<number>(-1);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(-1);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);
  const [baselineWorkflows, setBaselineWorkflows] = useState<WorkflowData | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialogState>({
    isOpen: false,
    type: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  React.useEffect(() => {
    if (workflows.length > 0 && !currentFlow) {
      setCurrentFlow(workflows[0]);
      setCurrentPhaseIndex(0);
      setCurrentStepIndex(0);
    }
  }, [workflows, currentFlow]);

  const setCurrentPhase = (phaseIndex: number) => {
    setCurrentPhaseIndex(phaseIndex);
    setCurrentStepIndex(-1);
  };

  const setCurrentStep = (stepIndex: number) => {
    setCurrentStepIndex(stepIndex);
  };

  const updateFlow = (updatedFlow: WorkflowFlow) => {
    if (!currentFlow) return;
    const updatedWorkflows = workflows.map(flow => flow === currentFlow ? updatedFlow : flow);
    setWorkflows(updatedWorkflows);
    setCurrentFlow(updatedFlow);
    // Mark as dirty when any part of the current flow changes (e.g., WYSIWYG content edits)
    setHasUnsavedChanges(true);
  };

  const addPhase = (flowIndex: number) => {
    const newPhase: WorkflowPhase = { name: "Nouvelle phase", steps: [] };
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases.push(newPhase);
    setWorkflows(updatedWorkflows);
  };

  const addPhaseAfter = (flowIndex: number, phaseIndex: number) => {
    const newPhase: WorkflowPhase = { name: "Nouvelle phase", steps: [] };
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases.splice(phaseIndex + 1, 0, newPhase);
    setWorkflows(updatedWorkflows);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
      setCurrentPhase(phaseIndex + 1);
    }
  };

  const removePhase = (flowIndex: number, phaseIndex: number) => {
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases.splice(phaseIndex, 1);
    setWorkflows(updatedWorkflows);
    if (currentPhaseIndex === phaseIndex) {
      setCurrentPhase(-1);
    }
  };

  const addStep = (flowIndex: number, phaseIndex: number) => {
    const newStep: WorkflowStep = { name: "Nouvelle étape", blocks: { instruction: { text: "Nouvelle instruction..." } } };
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases[phaseIndex].steps.push(newStep);
    setWorkflows(updatedWorkflows);
  };

  const addStepAfter = (flowIndex: number, phaseIndex: number, stepIndex: number, stepContent?: WorkflowStep) => {
    const newStep: WorkflowStep = stepContent || { name: "Nouvelle étape", blocks: { instruction: { text: "Nouvelle instruction..." } } };
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases[phaseIndex].steps.splice(stepIndex + 1, 0, newStep);
    setWorkflows(updatedWorkflows);
  };

  const removeStep = (flowIndex: number, phaseIndex: number, stepIndex: number) => {
    const updatedWorkflows = [...workflows];
    updatedWorkflows[flowIndex].phases[phaseIndex].steps.splice(stepIndex, 1);
    setWorkflows(updatedWorkflows);
    if (currentStepIndex === stepIndex) {
      setCurrentStep(-1);
    }
  };

  const duplicatePhase = (flowIndex: number, phaseIndex: number) => {
    const updatedWorkflows = [...workflows];
    const phaseToClone = updatedWorkflows[flowIndex].phases[phaseIndex];
    const clonedPhase = JSON.parse(JSON.stringify(phaseToClone));
    clonedPhase.name = `${clonedPhase.name} (Copie)`;
    updatedWorkflows[flowIndex].phases.splice(phaseIndex + 1, 0, clonedPhase);
    setWorkflows(updatedWorkflows);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  };

  const movePhase = (flowIndex: number, phaseIndex: number, direction: 'up' | 'down') => {
    const updatedWorkflows = [...workflows];
    const phases = updatedWorkflows[flowIndex].phases;
    const newIndex = direction === 'up' ? phaseIndex - 1 : phaseIndex + 1;
    if (newIndex >= 0 && newIndex < phases.length) {
      [phases[phaseIndex], phases[newIndex]] = [phases[newIndex], phases[phaseIndex]];
      setWorkflows(updatedWorkflows);
      if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
        setCurrentFlow(updatedWorkflows[flowIndex]);
        if (currentPhaseIndex === phaseIndex) setCurrentPhaseIndex(newIndex);
        else if (currentPhaseIndex === newIndex) setCurrentPhaseIndex(phaseIndex);
      }
    }
  };

  const duplicateStep = (flowIndex: number, phaseIndex: number, stepIndex: number) => {
    const updatedWorkflows = [...workflows];
    const stepToClone = updatedWorkflows[flowIndex].phases[phaseIndex].steps[stepIndex];
    const clonedStep = JSON.parse(JSON.stringify(stepToClone));
    clonedStep.name = `${clonedStep.name} (Copie)`;
    updatedWorkflows[flowIndex].phases[phaseIndex].steps.splice(stepIndex + 1, 0, clonedStep);
    setWorkflows(updatedWorkflows);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  };

  const moveStep = (flowIndex: number, phaseIndex: number, stepIndex: number, direction: 'up' | 'down') => {
    const updatedWorkflows = [...workflows];
    const phases = updatedWorkflows[flowIndex].phases;
    const steps = phases[phaseIndex].steps;

    if (direction === 'up') {
      if (stepIndex > 0) {
        // Swap within the same phase
        [steps[stepIndex], steps[stepIndex - 1]] = [steps[stepIndex - 1], steps[stepIndex]];
      } else if (phaseIndex > 0) {
        // Move to the end of the previous phase
        const [step] = steps.splice(0, 1);
        phases[phaseIndex - 1].steps.push(step);
      } else {
        return;
      }
    } else {
      if (stepIndex < steps.length - 1) {
        // Swap within the same phase
        [steps[stepIndex], steps[stepIndex + 1]] = [steps[stepIndex + 1], steps[stepIndex]];
      } else if (phaseIndex < phases.length - 1) {
        // Move to the start of the next phase
        const [step] = steps.splice(stepIndex, 1);
        phases[phaseIndex + 1].steps.unshift(step);
      } else {
        return;
      }
    }

    setWorkflows(updatedWorkflows);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  };

  const addToHistory = useCallback((action: string) => {
    const newEntry: WorkflowHistoryEntry = { workflows: JSON.parse(JSON.stringify(workflows)), timestamp: Date.now(), action };
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newEntry);
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setCurrentHistoryIndex(currentHistoryIndex + 1);
    }
    setHistory(newHistory);
    setHasUnsavedChanges(true);
  }, [workflows, history, currentHistoryIndex]);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  }, []);

  const handleSaveAndExit = useCallback(async () => {
    try {
      if (onSave) {
        await onSave(workflows);
      }
      setHasUnsavedChanges(false);
      setIsEditMode(false);
      setBaselineWorkflows(null);
      closeConfirmDialog();
    } catch (e) {
      console.error('Error saving changes:', e);
    }
  }, [onSave, workflows, closeConfirmDialog]);

  const handleDiscardAndExit = useCallback(() => {
    if (baselineWorkflows) {
      setWorkflows(JSON.parse(JSON.stringify(baselineWorkflows)));
      if (baselineWorkflows.length > 0) {
        setCurrentFlow(baselineWorkflows[0]);
      }
    }
    setHasUnsavedChanges(false);
    setIsEditMode(false);
    setBaselineWorkflows(null);
    closeConfirmDialog();
  }, [baselineWorkflows, closeConfirmDialog]);

  const toggleEditMode = useCallback(async () => {
    if (!isEditMode) {
      // Entering edit mode: capture baseline snapshot for possible discard
      setBaselineWorkflows(JSON.parse(JSON.stringify(workflows)));
      setIsEditMode(true);
      return;
    }

    // Leaving edit mode
    if (hasUnsavedChanges) {
      // Show save confirmation dialog
      setConfirmDialog({
        isOpen: true,
        type: 'save',
        onConfirm: handleSaveAndExit,
        onCancel: handleDiscardAndExit,
      });
      return;
    }

    setIsEditMode(false);
    setBaselineWorkflows(null);
  }, [isEditMode, hasUnsavedChanges, workflows, handleSaveAndExit, handleDiscardAndExit]);

  const saveChanges = useCallback(() => {
    if (onSave) {
      try {
        const maybePromise = onSave(workflows);
        if (maybePromise && typeof (maybePromise as any).then === 'function') {
          (maybePromise as Promise<void>).finally(() => setHasUnsavedChanges(false));
        } else {
          setHasUnsavedChanges(false);
        }
      } catch {
        // leave hasUnsavedChanges as-is on error
      }
    } else {
      setHasUnsavedChanges(false);
    }
  }, [onSave, workflows]);

  const canUndo = useCallback(() => currentHistoryIndex >= 0, [currentHistoryIndex]);
  const canRedo = useCallback(() => currentHistoryIndex < history.length - 1, [currentHistoryIndex, history.length]);

  const undo = useCallback(() => {
    if (canUndo()) {
      const previousState = history[currentHistoryIndex];
      setWorkflows(previousState.workflows);
      setCurrentHistoryIndex(currentHistoryIndex - 1);
      setHasUnsavedChanges(true);
    }
  }, [canUndo, history, currentHistoryIndex]);

  const redo = useCallback(() => {
    if (canRedo()) {
      const nextState = history[currentHistoryIndex + 1];
      setWorkflows(nextState.workflows);
      setCurrentHistoryIndex(currentHistoryIndex + 1);
      setHasUnsavedChanges(true);
    }
  }, [canRedo, history, currentHistoryIndex]);

  const toggleStepBlockType = (flowIndex: number, phaseIndex: number, stepIndex: number, blockType: 'instruction'): BlockToggleResult => {
    const step = workflows[flowIndex].phases[phaseIndex].steps[stepIndex];
    if (step.blocks[blockType]) {
      let hasContent = false;
      if (blockType === 'instruction' && step.blocks.instruction) {
        hasContent = step.blocks.instruction.text.trim() !== '' && step.blocks.instruction.text.trim() !== 'Nouvelle instruction...';
      }
      if (hasContent) {
        return { needsDeactivationConfirmation: true, blockToDeactivate: blockType };
      }
      delete step.blocks[blockType];
      setWorkflows([...workflows]);
      return {};
    } else {
      updateStepBlockType(flowIndex, phaseIndex, stepIndex, blockType);
      return {};
    }
  };

  const updateStepBlockType = useCallback((flowIndex: number, phaseIndex: number, stepIndex: number, blockType: 'instruction') => {
    addToHistory('Changement de type de bloc');
    const updatedWorkflows = [...workflows];
    const step = updatedWorkflows[flowIndex].phases[phaseIndex].steps[stepIndex];
    if (blockType === 'instruction' && !step.blocks.instruction) {
      step.blocks.instruction = { text: 'Nouvelle instruction...' };
    }
    setWorkflows(updatedWorkflows);
    setHasUnsavedChanges(true);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  }, [workflows, currentFlow, addToHistory]);

  const convertStepBlockType = useCallback((flowIndex: number, phaseIndex: number, stepIndex: number, fromType: 'instruction', toType: 'instruction') => {
    addToHistory('Conversion de type de bloc');
    const updatedWorkflows = [...workflows];
    const step = updatedWorkflows[flowIndex].phases[phaseIndex].steps[stepIndex];
    if (toType === 'instruction' && !step.blocks.instruction) {
      step.blocks.instruction = { text: 'Nouvelle instruction...' };
    }
    setWorkflows(updatedWorkflows);
    setHasUnsavedChanges(true);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  }, [workflows, currentFlow, addToHistory]);

  const deactivateStepBlock = useCallback((flowIndex: number, phaseIndex: number, stepIndex: number, blockType: 'instruction') => {
    addToHistory(`Bloc ${blockType} supprimé`);
    const updatedWorkflows = [...workflows];
    const step = updatedWorkflows[flowIndex].phases[phaseIndex].steps[stepIndex];
    delete step.blocks[blockType];
    setWorkflows(updatedWorkflows);
    setHasUnsavedChanges(true);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      setCurrentFlow(updatedWorkflows[flowIndex]);
    }
  }, [workflows, currentFlow, addToHistory]);

  const convertStepToPhase = useCallback((flowIndex: number, phaseIndex: number, stepIndex: number) => {
    const updatedWorkflows = [...workflows];
    const originalPhase = updatedWorkflows[flowIndex].phases[phaseIndex];
    const step = originalPhase.steps[stepIndex];
    
    // Get the converted step and all subsequent steps
    const stepsForNewPhase = originalPhase.steps.slice(stepIndex);
    
    // Create new phase with step's name and include converted step + subsequent steps
    const newPhase: WorkflowPhase = { 
      name: step.name, 
      steps: stepsForNewPhase 
    };
    
    // Remove converted step and all subsequent steps from original phase
    originalPhase.steps = originalPhase.steps.slice(0, stepIndex);
    
    // If original phase is now empty, add a placeholder step
    if (originalPhase.steps.length === 0) {
      originalPhase.steps.push({ 
        name: 'Nouvelle étape', 
        blocks: { instruction: { text: '' } } 
      });
    }
    
    // Insert the new phase after the current phase
    updatedWorkflows[flowIndex].phases.splice(phaseIndex + 1, 0, newPhase);
    
    setWorkflows(updatedWorkflows);
    setHasUnsavedChanges(true);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      const newCurrentFlow = updatedWorkflows[flowIndex];
      setCurrentFlow(newCurrentFlow);
      setCurrentPhase(phaseIndex + 1);
      setCurrentStep(0);
    }
  }, [workflows, currentFlow]);

  const convertPhaseToStep = useCallback((flowIndex: number, phaseIndex: number, _targetPhaseIndex?: number) => {
    const updatedWorkflows = [...workflows];
    const phase = updatedWorkflows[flowIndex].phases[phaseIndex];
    
    // Create the converted step from the phase
    // Note: WorkflowPhase doesn't have description, so instruction text is empty
    const convertedStep: WorkflowStep = { 
      name: phase.name, 
      blocks: { 
        instruction: { text: '' } 
      } 
    };
    
    // All steps to move: the converted step first, then existing steps from the phase
    const stepsToMove = [convertedStep, ...phase.steps];
    
    if (phaseIndex === 0) {
      // No previous phase - create a placeholder phase
      const placeholderPhase: WorkflowPhase = { 
        name: 'Phase title', 
        steps: stepsToMove 
      };
      // Replace the phase being converted with the placeholder
      updatedWorkflows[flowIndex].phases.splice(phaseIndex, 1, placeholderPhase);
    } else {
      // Has previous phase - add steps to the previous phase
      const previousPhaseIdx = phaseIndex - 1;
      updatedWorkflows[flowIndex].phases[previousPhaseIdx].steps.push(...stepsToMove);
      // Remove the converted phase
      updatedWorkflows[flowIndex].phases.splice(phaseIndex, 1);
    }
    
    setWorkflows(updatedWorkflows);
    setHasUnsavedChanges(true);
    if (currentFlow && flowIndex === workflows.findIndex(f => f === currentFlow)) {
      const newCurrentFlow = updatedWorkflows[flowIndex];
      setCurrentFlow(newCurrentFlow);
      if (phaseIndex === 0) {
        setCurrentPhase(0);
        setCurrentStep(0);
      } else {
        setCurrentPhase(phaseIndex - 1);
        setCurrentStep(updatedWorkflows[flowIndex].phases[phaseIndex - 1].steps.length - stepsToMove.length);
      }
    }
  }, [workflows, currentFlow]);

  return (
    <WorkflowContext.Provider value={{
      workflows,
      currentFlow,
      currentPhaseIndex,
      currentStepIndex,
      isEditMode,
      hasUnsavedChanges,
      history,
      currentHistoryIndex,
      setWorkflows,
      setCurrentFlow,
      setCurrentPhase,
      setCurrentStep,
      updateFlow,
      addPhase,
      addPhaseAfter,
      removePhase,
      addStep,
      addStepAfter,
      removeStep,
      duplicatePhase,
      movePhase,
      duplicateStep,
      moveStep,
      toggleEditMode,
      saveChanges,
      canUndo,
      canRedo,
      undo,
      redo,
      addToHistory,
      updateStepBlockType,
      toggleStepBlockType,
      convertStepBlockType,
      deactivateStepBlock,
      convertStepToPhase,
      convertPhaseToStep
    }}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <>
          {/* Save confirmation dialog */}
          <AlertDialog
            isOpen={confirmDialog.isOpen && confirmDialog.type === 'save'}
            onClose={closeConfirmDialog}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
            title="Sauvegarder les modifications"
            message="Vous avez des modifications non sauvegardées. Voulez-vous les sauvegarder ?"
            confirmText="Sauvegarder"
            cancelText="Non, abandonner"
            variant="warning"
            confirmButtonVariant="primary"
          />
        </>,
        document.body
      )}
    </WorkflowContext.Provider>
  );
}

export function useWorkflow() {
  const context = useContext(WorkflowContext);
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider');
  }
  return context;
}


